import { useState, useEffect, useMemo } from "react";
import { AppVariablesService } from "../../services/AppVariablesService";
import { Link } from "react-router-dom";

export default function Rules() {
  const [config, setConfig] = useState({
    checkinAutoCancelCron: "23:59", // Default for display
    maxMonthlyHours: 120,
    maxWeeklyHours: 15,
    maxMonthlyHoursForEvents: 120, // Rule 12 - Monthly hours limit for events
    maxWeeklyHoursForEvents: 15, // Rule 12 - Weekly hours limit for events
    minHoursBeforeCancellation: "",
    maxCancellationsPerMonth: "",
    cancellationBlockDays: "",
    termsOfUseUrl: "",
    checkinRadiusKm: "",
    checkinTimeToleranceMinutes: "",
    alwaysShowCheckinButton: false, // New rule - default disabled
    requireLocationForCheckin: false, // Rule 9 - default disabled
    showNovember2025Calendar: false, // Rule 10 - default disabled
    nextMonthReleaseDay: 24, // Rule 11 - Day of month when next month becomes available
    nextMonthReleaseTime: "13:00", // Rule 11 - Time when next month becomes available
    minHoursBetweenReservations: 0, // Rule 13 - Minimum gap between a user's reservations on the same day
  });

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const appVariablesService = useMemo(() => new AppVariablesService(), []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await appVariablesService.find();
        if (response?.config) {
          const cronParts = response.config.checkinAutoCancelCron.split(" ");
          const formattedTime = `${cronParts[2].padStart(
            2,
            "0"
          )}:${cronParts[1].padStart(2, "0")}`;

          setConfig({
            checkinAutoCancelCron: formattedTime,
            maxMonthlyHours: response.config.maxMonthlyHours || 120,
            maxWeeklyHours: response.config.maxWeeklyHours || 15,
            maxMonthlyHoursForEvents: response.config.maxMonthlyHoursForEvents || 120,
            maxWeeklyHoursForEvents: response.config.maxWeeklyHoursForEvents || 15,
            minHoursBeforeCancellation:
              response.config.minHoursBeforeCancellation,
            maxCancellationsPerMonth: response.config.maxCancellationsPerMonth,
            cancellationBlockDays: response.config.cancellationBlockDays,
            termsOfUseUrl: response.config.termsOfUseUrl,
            checkinRadiusKm: response.config.checkinRadiusKm || "",
            checkinTimeToleranceMinutes: response.config.checkinTimeToleranceMinutes || "",
            alwaysShowCheckinButton: response.config.alwaysShowCheckinButton || false,
            requireLocationForCheckin: response.config.requireLocationForCheckin || false,
            showNovember2025Calendar: response.config.showNovember2025Calendar || false,
            nextMonthReleaseDay: response.config.nextMonthReleaseDay || 24,
            nextMonthReleaseTime: response.config.nextMonthReleaseTime || "13:00",
            minHoursBetweenReservations:
              response.config.minHoursBetweenReservations ?? 0,
          });
        }
      } catch (err) {
        setError("Erro ao carregar as regras.");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [appVariablesService]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Convert nextMonthReleaseDay to number
    const numericFields = ['nextMonthReleaseDay', 'minHoursBetweenReservations'];
    const processedValue = numericFields.includes(name) ? Number(value) : value;
    setConfig({ ...config, [name]: processedValue });
  };

  const handleSave = async () => {
    setSuccess(false);
    setError("");

    // Ensure checkinAutoCancelCron is formatted correctly before saving
    const [hours, minutes] = config.checkinAutoCancelCron.split(":");
    const formattedCron = `59 ${minutes} ${hours} * * *`;

    const updatedConfig = {
      ...config,
      checkinAutoCancelCron: formattedCron,
    };

    try {
      await appVariablesService.updateVariable({ config: updatedConfig });
      setSuccess(true);
    } catch (err) {
      setError("Erro ao salvar as regras.");
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Carregando regras...</div>;
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Regras de Negócio</h2>

      <div className="d-flex flex-column gap-3">
        <div className="d-flex align-items-center gap-2">
          <span>1 - Todos os dias às</span>
          <input
            type="time"
            className="form-control w-auto"
            name="checkinAutoCancelCron"
            value={config.checkinAutoCancelCron}
            onChange={handleChange}
          />
          <span>
            Verificar todas as escalas do dia e, nas que não tem checkin,
            colocar status = CANCELADO
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>2 - GCM só pode marcar no máximo</span>
          <input
            type="number"
            className="form-control"
            name="maxMonthlyHours"
            value={config.maxMonthlyHours}
            onChange={handleChange}
            style={{ width: "70px" }}
          />
          <span>horas por mês</span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>3 - GCM pode reservar</span>
          <input
            type="number"
            className="form-control"
            name="maxWeeklyHours"
            value={config.maxWeeklyHours}
            onChange={handleChange}
            style={{ width: "60px" }}
          />
          <span>horas por semana</span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>4 - Cancelamento permitido com até</span>
          <input
            type="number"
            className="form-control"
            name="minHoursBeforeCancellation"
            value={config.minHoursBeforeCancellation}
            onChange={handleChange}
            style={{ width: "60px" }}
          />
          <span>horas de antecedência</span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>5 - Se o GCM cancelar mais de</span>
          <input
            type="number"
            className="form-control"
            name="maxCancellationsPerMonth"
            value={config.maxCancellationsPerMonth}
            onChange={handleChange}
            style={{ width: "60px" }}
          />
          <span>vezes no mês, ele fica automaticamente bloqueado</span>
          <input
            type="number"
            className="form-control"
            name="cancellationBlockDays"
            value={config.cancellationBlockDays}
            onChange={handleChange}
            style={{ width: "60px" }}
          />
          <span>dias corridos</span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>
            6 - Para atualizar os Termos de Uso{" "}
            <Link to="/admin/update-terms">clique aqui</Link>
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>7 - Permitir Checkin dentro de um raio de</span>
          <input
            type="number"
            className="form-control"
            name="checkinRadiusKm"
            value={config.checkinRadiusKm}
            onChange={handleChange}
            style={{ width: "60px" }}
            min="0"
            step="0.1"
          />
          <span>km e com até</span>
          <input
            type="number"
            className="form-control"
            name="checkinTimeToleranceMinutes"
            value={config.checkinTimeToleranceMinutes}
            onChange={handleChange}
            style={{ width: "60px" }}
            min="0"
          />
          <span>min antes/depois</span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>8 - Sempre exibir botão de checkin para reservas do dia</span>
          <div className="form-check form-switch ms-2">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="alwaysShowCheckinButton"
              name="alwaysShowCheckinButton"
              checked={config.alwaysShowCheckinButton}
              onChange={(e) => setConfig({ ...config, alwaysShowCheckinButton: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="alwaysShowCheckinButton">
              {config.alwaysShowCheckinButton ? "Habilitado" : "Desabilitado"}
            </label>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>9 - Exigir localização para checkin/checkout</span>
          <div className="form-check form-switch ms-2">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="requireLocationForCheckin"
              name="requireLocationForCheckin"
              checked={config.requireLocationForCheckin}
              onChange={(e) => setConfig({ ...config, requireLocationForCheckin: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="requireLocationForCheckin">
              {config.requireLocationForCheckin ? "Habilitado" : "Desabilitado"}
            </label>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>10 - Mostrar calendário de Novembro 2025?</span>
          <div className="form-check form-switch ms-2">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="showNovember2025Calendar"
              name="showNovember2025Calendar"
              checked={config.showNovember2025Calendar}
              onChange={(e) => setConfig({ ...config, showNovember2025Calendar: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="showNovember2025Calendar">
              {config.showNovember2025Calendar ? "Sim (visível)" : "Não (oculto)"}
            </label>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>11 - Liberar próximo mês no dia</span>
          <input
            type="number"
            className="form-control"
            name="nextMonthReleaseDay"
            value={config.nextMonthReleaseDay}
            onChange={handleChange}
            style={{ width: "60px" }}
            min="1"
            max="31"
          />
          <span>de cada mês às</span>
          <input
            type="time"
            className="form-control w-auto"
            name="nextMonthReleaseTime"
            value={config.nextMonthReleaseTime}
            onChange={handleChange}
          />
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold">12 - Limites de Carga Horária para Eventos:</span>
        </div>

        <div className="d-flex align-items-center gap-2 ms-3">
          <span>• GCM pode marcar no máximo</span>
          <input
            type="number"
            className="form-control"
            name="maxMonthlyHoursForEvents"
            value={config.maxMonthlyHoursForEvents}
            onChange={handleChange}
            style={{ width: "70px" }}
          />
          <span>horas por mês em eventos</span>
        </div>

        <div className="d-flex align-items-center gap-2 ms-3">
          <span>• GCM pode reservar</span>
          <input
            type="number"
            className="form-control"
            name="maxWeeklyHoursForEvents"
            value={config.maxWeeklyHoursForEvents}
            onChange={handleChange}
            style={{ width: "60px" }}
          />
          <span>horas por semana em eventos</span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span>
            13 - Intervalo mínimo de
          </span>
          <input
            type="number"
            className="form-control"
            name="minHoursBetweenReservations"
            value={config.minHoursBetweenReservations}
            onChange={handleChange}
            style={{ width: "70px" }}
            min="0"
            step="0.5"
          />
          <span>
            horas entre reservas do mesmo GCM no mesmo dia (impede reservar em
            escolas/eventos diferentes sem essa folga; 0 = desabilitado)
          </span>
        </div>
      </div>

      <div className="d-flex gap-3 mt-4 justify-content-end">
        {success && (
          <div className="text-success mt-3">
            Alterações Salvas com Sucesso!
          </div>
        )}
        {error && <div className="text-danger mt-3">{error}</div>}

        <button
          className="btn btn-danger"
          onClick={() => window.location.reload()}
        >
          Cancelar
        </button>
        <button className="btn btn-primary" onClick={handleSave}>
          Salvar
        </button>
      </div>
    </div>
  );
}
